/** QuickHouse — Helpers géométriques 2D purs (unités : cm) */

// ── Murs (segments d'axe) ─────────────────────────────────────
export function wallLength(wall) {
  return Math.hypot(wall.b.x - wall.a.x, wall.b.y - wall.a.y);
}

export function wallAngle(wall) {
  return Math.atan2(wall.b.y - wall.a.y, wall.b.x - wall.a.x);
}

export function wallMidpoint(wall) {
  return { x: (wall.a.x + wall.b.x) / 2, y: (wall.a.y + wall.b.y) / 2 };
}

/** Point sur l'axe du mur à la distance u de a */
export function pointOnWall(wall, u) {
  const len = wallLength(wall);
  if (len === 0) return { ...wall.a };
  const t = u / len;
  return { x: wall.a.x + (wall.b.x - wall.a.x) * t, y: wall.a.y + (wall.b.y - wall.a.y) * t };
}

// ── Distances / projections ───────────────────────────────────
export function dist(p, q) {
  return Math.hypot(q.x - p.x, q.y - p.y);
}

/** Distance d'un point au segment [a,b] */
export function distPointToSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return dist(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return dist(p, { x: a.x + t * dx, y: a.y + t * dy });
}

/**
 * Projette un point sur l'axe d'un mur.
 * Retourne { u, dist } : u = abscisse le long de l'axe (clampée à [0, len]),
 * dist = distance du point à l'axe (non clampée).
 */
export function projectOnWall(wall, p) {
  const dx = wall.b.x - wall.a.x;
  const dy = wall.b.y - wall.a.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return { u: 0, dist: dist(p, wall.a) };
  const u = ((p.x - wall.a.x) * dx + (p.y - wall.a.y) * dy) / len;
  const d = Math.abs((p.x - wall.a.x) * (-dy / len) + (p.y - wall.a.y) * (dx / len));
  return { u: Math.max(0, Math.min(len, u)), dist: d };
}

// ── Précision ─────────────────────────────────────────────────
/** Arrondit une valeur en cm à la précision du modèle : 0,1 cm = 1 mm */
export function roundCoord(v) {
  return Math.round(v * 10) / 10;
}

/** Formatte une longueur en cm : entier tel quel, sinon une décimale (mm) */
export function fmtLen(v) {
  const r = Math.round(v * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

// ── Snapping ──────────────────────────────────────────────────
export function snapToGrid(p, gridSize) {
  return {
    x: roundCoord(Math.round(p.x / gridSize) * gridSize),
    y: roundCoord(Math.round(p.y / gridSize) * gridSize),
  };
}

/** Abscisse NON CLAMPÉE d'un point projeté sur l'axe d'un mur (peut être < 0 ou > longueur) */
export function projectU(wall, p) {
  const dx = wall.b.x - wall.a.x;
  const dy = wall.b.y - wall.a.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return 0;
  return ((p.x - wall.a.x) * dx + (p.y - wall.a.y) * dy) / len;
}

/** Snap au point candidat le plus proche sous tolérance, sinon null */
export function snapToPoints(p, candidates, tol) {
  let best = null;
  let bestD = tol;
  for (const c of candidates) {
    const d = dist(p, c);
    if (d <= bestD) { best = c; bestD = d; }
  }
  return best ? { x: best.x, y: best.y } : null;
}

/** Alignement axial H/V par rapport à un point de départ (sous tolérance, ou forcé) */
export function axisAlign(start, p, tol, force = false) {
  const dx = Math.abs(p.x - start.x);
  const dy = Math.abs(p.y - start.y);
  if (force) {
    return dx >= dy ? { x: p.x, y: start.y } : { x: start.x, y: p.y };
  }
  if (dy <= tol && dx > dy) return { x: p.x, y: start.y };
  if (dx <= tol && dy > dx) return { x: start.x, y: p.y };
  return null;
}

/** Hit-test : le mur le plus proche du point sous tolérance (retourne { wall, u, dist } ou null) */
export function hitWall(walls, p, tol) {
  let best = null;
  for (const wall of walls) {
    const d = distPointToSegment(p, wall.a, wall.b);
    const limit = wall.thickness / 2 + tol;
    if (d <= limit && (!best || d < best.dist)) {
      const proj = projectOnWall(wall, p);
      best = { wall, u: proj.u, dist: d };
    }
  }
  return best;
}

/** Bounding box d'une liste de murs (axes + demi-épaisseurs), ou null si vide */
export function wallsBounds(walls) {
  if (!walls.length) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const w of walls) {
    const half = w.thickness / 2;
    for (const p of [w.a, w.b]) {
      minX = Math.min(minX, p.x - half);
      minY = Math.min(minY, p.y - half);
      maxX = Math.max(maxX, p.x + half);
      maxY = Math.max(maxY, p.y + half);
    }
  }
  return { minX, minY, maxX, maxY };
}
